/**
 * Row balancing for the My Dials tile layout (KEY-516).
 *
 * The ticket asks for tiles laid out "max 6 wide, always keeping some balance between rows (so if 7 in
 * a group, row of 4 then 3, NOT 6 and 1)". Neither `flex-wrap` nor `grid-auto-fill` can do that: they
 * fill each row to the cap and leave the remainder stranded, so seven tiles wrap to 6 + 1. So we
 * compute the row lengths ourselves and render one flex row per computed length.
 *
 * Pure — no React, no DOM — so it can be unit-tested on its own.
 */

/** The widest a row of tiles ever gets, from the ticket. */
export const MAX_TILES_PER_ROW = 6;

/** Gap between tiles, in px. Mirrors `--kd-tile-gap` in theme.css; used to derive the responsive cap. */
export const TILE_GAP = 14;

/** The narrowest a tile may be before we drop a column. Below this the ring stops being readable. */
export const MIN_TILE_WIDTH = 150;

/**
 * Splits `n` items into rows of at most `cap`, as evenly as possible, longest row first.
 *
 * Uses the fewest rows that respect the cap, which forces every row length into `{base, base + 1}` —
 * so the longest and shortest rows differ by at most one, the least imbalance achievable at that row
 * count. `base + 1` can never breach the cap: `rows = ceil(n / cap)` gives `n <= cap * rows` and hence
 * `base <= cap`, and when `base === cap` the division is exact, so there is no remainder to hand out.
 *
 * 7 at cap 6 → [4, 3] (plain wrapping would give [6, 1]); 13 → [5, 4, 4] (wrapping gives [6, 6, 1]).
 */
export function balanceRowLengths(n: number, maxPerRow: number = MAX_TILES_PER_ROW): number[] {
  if (!Number.isFinite(n) || n <= 0) return [];
  const count = Math.floor(n);
  if (count <= 0) return [];
  // A zero/negative/NaN cap would divide by zero or loop forever — one per row is the sane floor.
  const cap = Number.isFinite(maxPerRow) ? Math.max(1, Math.floor(maxPerRow)) : 1;

  const rows = Math.ceil(count / cap);
  const base = Math.floor(count / rows);
  const remainder = count % rows;

  // Longer rows first — the ticket's "row of 4 then 3", not "3 then 4".
  return [
    ...Array.from({ length: remainder }, () => base + 1),
    ...Array.from({ length: rows - remainder }, () => base),
  ];
}

/** Applies {@link balanceRowLengths} to a list, preserving order. Flattening the result returns the input. */
export function balanceRows<T>(items: readonly T[], maxPerRow: number = MAX_TILES_PER_ROW): T[][] {
  const lengths = balanceRowLengths(items.length, maxPerRow);
  const rows: T[][] = [];
  let cursor = 0;
  for (const length of lengths) {
    rows.push(items.slice(cursor, cursor + length));
    cursor += length;
  }
  return rows;
}

/**
 * How many tiles fit across `width` px before they get too small to read — capped at
 * {@link MAX_TILES_PER_ROW} and never below 1, so a narrow phone gets a single column rather than none.
 */
export function tilesPerRow(width: number, gap: number = TILE_GAP, minTile: number = MIN_TILE_WIDTH): number {
  if (!Number.isFinite(width) || width <= 0) return 1;
  // n tiles occupy n*minTile + (n-1)*gap, so n = (width + gap) / (minTile + gap).
  const fit = Math.floor((width + gap) / (minTile + gap));
  return Math.min(MAX_TILES_PER_ROW, Math.max(1, fit));
}
